import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import BranchSelect from "@/components/BranchSelect";
import BranchFilter from "@/components/BranchFilter";
import ModelCategoryFilter from "@/components/ModelCategoryFilter";
import DateRangeFilter from "@/components/DateRangeFilter";
import RelatedVouchers from "@/components/RelatedVouchers";
import BookingReceiptButton from "@/components/BookingReceiptButton";
import SaleFormDrawer from "@/components/SaleFormDrawer";
import SwapVehicleDialog from "@/components/SwapVehicleDialog";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import {
  Plus,
  CaretDown,
  CaretRight,
  X,
  CurrencyInr,
  Prohibit,
  ArrowsClockwise,
  ArrowsLeftRight,
  Ticket,
} from "@phosphor-icons/react";

const STATUS_META = {
  active: { l: "ACTIVE", cls: "border-[#00B341] bg-[#00B341]/10 text-[#00B341]" },
  delivered: { l: "DELIVERED", cls: "border-ink bg-canvas-panel text-ink" },
  cancelled: { l: "CANCELLED", cls: "border-destructive bg-destructive/10 text-destructive" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.active;
  return (
    <span
      data-testid={`booking-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${meta.cls}`}
    >
      {meta.l}
    </span>
  );
}

const STATUS_FILTERS = [
  { v: "", l: "All" },
  { v: "active", l: "Active" },
  { v: "delivered", l: "Delivered" },
  { v: "cancelled", l: "Cancelled" },
];

function BookingFormDrawer({ open, vehicles, customers, packages, onClose, onSaved }) {
  const blank = {
    package_id: "",
    color: "",
    customer_id: "",
    branch_id: "",
    gst_type: "intra",
    package_ids: [],
    expected_delivery_date: "",
    initial_deposit: "",
    initial_payment_method: "cash",
    initial_payment_reference: "",
    notes: "",
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    if (open) setForm(blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const togglePkg = (pkgId) => {
    setForm((f) => {
      const has = f.package_ids.includes(pkgId);
      return {
        ...f,
        package_ids: has ? f.package_ids.filter((id) => id !== pkgId) : [...f.package_ids, pkgId],
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!(await confirm({ title: "Save booking?", body: "This booking will be created. Confirm details before saving." }))) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        initial_deposit: Number(form.initial_deposit) || 0,
        branch_id: form.branch_id || null,
        // Bookings are by model (package) + color, not physical VIN.
        vehicle_id: null,
      };
      await api.post("/bookings", body);
      toast.success("Booking created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        data-testid="booking-form-drawer"
        className="w-[500px] bg-canvas-card border-l-2 border-ink overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-canvas-line flex items-center justify-between sticky top-0 bg-canvas-card z-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
              New booking
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1">Reserve a vehicle</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 border border-canvas-line hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Model / Package <span className="text-destructive">*</span>
            </label>
            <select
              required
              value={form.package_id}
              onChange={(e) => set("package_id")(e.target.value)}
              data-testid="booking-package"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="">— Select model / package —</option>
              {packages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <p className="font-mono text-[10px] text-ink-subtle mt-1">
              Booking is against the model/variant. Specific vehicle is assigned at delivery.
            </p>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Color <span className="text-destructive">*</span>
            </label>
            <input
              required
              value={form.color}
              onChange={(e) => set("color")(e.target.value)}
              placeholder="e.g., Aristocratic Red"
              data-testid="booking-color"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Customer <span className="text-destructive">*</span>
            </label>
            <select
              required
              value={form.customer_id}
              onChange={(e) => set("customer_id")(e.target.value)}
              data-testid="booking-customer"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="">— Select customer —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.phone ? `— ${c.phone}` : ""}
                </option>
              ))}
            </select>
          </div>

          <BranchSelect
            value={form.branch_id}
            onChange={(id) => set("branch_id")(id)}
            testId="booking-branch"
          />

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              GST Type
            </label>
            <select
              value={form.gst_type}
              onChange={(e) => set("gst_type")(e.target.value)}
              data-testid="booking-gst-type"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="intra">Intra-state (IGST + CGST)</option>
              <option value="inter">Inter-state (IGST only)</option>
            </select>
          </div>

          {packages.length > 0 && (
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Packages
              </label>
              <div className="border border-canvas-line max-h-44 overflow-y-auto">
                {packages.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-2 px-3 py-2 border-b border-canvas-line last:border-b-0 cursor-pointer hover:bg-canvas-panel/40"
                  >
                    <input
                      type="checkbox"
                      checked={form.package_ids.includes(p.id)}
                      onChange={() => togglePkg(p.id)}
                      data-testid={`booking-pkg-${p.id}`}
                      className="accent-ink"
                    />
                    <span className="text-sm">{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Expected Delivery
            </label>
            <input
              type="date"
              value={form.expected_delivery_date}
              onChange={(e) => set("expected_delivery_date")(e.target.value)}
              data-testid="booking-delivery"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
          </div>

          <div className="border-t border-canvas-line pt-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
              Initial Deposit (Advance)
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number"
                placeholder="Amount (₹)"
                value={form.initial_deposit}
                onChange={(e) => set("initial_deposit")(e.target.value)}
                data-testid="booking-initial-amount"
                className="px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
              />
              <select
                value={form.initial_payment_method}
                onChange={(e) => set("initial_payment_method")(e.target.value)}
                data-testid="booking-initial-method"
                className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              >
                {["cash", "upi", "card", "online", "bank", "cheque"].map((m) => (
                  <option key={m} value={m}>
                    {m.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            {form.initial_payment_method !== "cash" && (
              <input
                value={form.initial_payment_reference}
                onChange={(e) => set("initial_payment_reference")(e.target.value)}
                placeholder="UTR / Txn ref (required for non-cash)"
                data-testid="booking-initial-ref"
                className="mt-2 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
              />
            )}
          </div>

          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Notes
            </label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              data-testid="booking-notes"
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
          </div>

          <div className="pt-4 border-t border-canvas-line flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="booking-save"
              className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
            >
              {saving ? "Saving…" : "Create booking"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function TopUpDialog({ open, booking, onClose, onDone }) {
  const [form, setForm] = useState({
    amount: "",
    payment_method: "cash",
    reference: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    if (open) setForm({ amount: "", payment_method: "cash", reference: "", note: "" });
  }, [open]);

  if (!open) return null;
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!(await confirm({ title: "Add this advance top-up?", body: "A payment line will be added to the booking ledger." }))) return;
    setSaving(true);
    try {
      await api.post(`/bookings/${booking.id}/payments`, {
        amount: Number(form.amount) || 0,
        payment_method: form.payment_method,
        reference: form.reference,
        note: form.note,
      });
      toast.success("Payment added");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        data-testid="topup-dialog"
        className="bg-canvas-card border-2 border-ink p-6 w-full max-w-md space-y-4"
      >
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            Add Advance Payment
          </div>
          <h2 className="font-display text-xl font-bold text-ink mt-1">Top up</h2>
          <div className="font-mono text-[10px] text-ink-muted mt-1">
            Advance collected so far: {fmtMoney(booking.total_paid)}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="number"
            required
            placeholder="Amount (₹)"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            data-testid="topup-amount"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
          />
          <select
            value={form.payment_method}
            onChange={(e) => setForm((f) => ({ ...f, payment_method: e.target.value }))}
            data-testid="topup-method"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          >
            {["cash", "upi", "card", "online", "bank", "cheque"].map((m) => (
              <option key={m} value={m}>
                {m.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
        {form.payment_method !== "cash" && (
          <input
            value={form.reference}
            onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
            placeholder="UTR / Txn ref (required)"
            data-testid="topup-ref"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
          />
        )}
        <input
          value={form.note}
          onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          placeholder="Note (optional)"
          data-testid="topup-note"
          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
        <div className="flex justify-end gap-2 pt-2 border-t border-canvas-line">
          <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="topup-save"
            className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
          >
            {saving ? "Saving…" : "Add payment"}
          </button>
        </div>
      </form>
    </div>
  );
}

function BookingRow({ booking, isAdmin, onTopUp, onConvert, onCancel, onSwap, onReopen }) {
  const [expanded, setExpanded] = useState(false);
  const v = booking.vehicle;
  const c = booking.customer;
  return (
    <>
      <tr
        data-testid={`booking-row-${booking.id}`}
        className="border-b border-canvas-line hover:bg-canvas-panel/40"
      >
        <td className="px-4 py-3">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            data-testid={`toggle-booking-${booking.id}`}
            className="p-1 border border-canvas-line hover:border-ink"
          >
            {expanded ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
          </button>
        </td>
        <td className="px-4 py-3 font-mono text-[11px] text-ink">{fmtDate(booking.created_at)}</td>
        <td className="px-4 py-3">
          <div className="text-sm font-semibold text-ink">
            {booking.package_name || "—"}
          </div>
          <div className="font-mono text-[11px] text-ink-muted uppercase tracking-wider">
            {v?.vin
              ? `${booking.color || v.color || "—"} · VIN ${v.vin}`
              : `${booking.color || "—"} · awaiting allocation`}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="text-sm text-ink">{c?.name || "—"}</div>
          <div className="font-mono text-[11px] text-ink-muted">{c?.phone}</div>
        </td>
        <td className="px-4 py-3 font-mono text-sm tabular-nums text-[#00B341]">
          {fmtMoney(booking.total_paid)}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={booking.status} />
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-1.5">
            <BookingReceiptButton booking={booking} />
            {booking.status === "active" && (
              <>
                <button
                  onClick={() => onTopUp(booking)}
                  data-testid={`topup-btn-${booking.id}`}
                  title="Top up advance"
                  className="p-1.5 border border-canvas-line hover:border-ink"
                >
                  <CurrencyInr size={12} weight="bold" />
                </button>
                <button
                  onClick={() => onConvert(booking)}
                  data-testid={`convert-btn-${booking.id}`}
                  title="Convert to sale"
                  className="p-1.5 border border-[#00B341] text-[#00B341] hover:bg-[#00B341] hover:text-white"
                >
                  <ArrowsClockwise size={12} weight="bold" />
                </button>
                {isAdmin && booking.vehicle_id && (
                  <button
                    onClick={() => onSwap(booking)}
                    data-testid={`swap-btn-${booking.id}`}
                    title="Swap to a different VIN (same package, same branch)"
                    className="p-1.5 border border-canvas-line text-ink-muted hover:border-ink hover:text-ink"
                  >
                    <ArrowsLeftRight size={12} weight="bold" />
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => onCancel(booking)}
                    data-testid={`cancel-btn-${booking.id}`}
                    title="Cancel booking (admin)"
                    className="p-1.5 border border-destructive text-destructive hover:bg-destructive hover:text-white"
                  >
                    <Prohibit size={12} weight="bold" />
                  </button>
                )}
              </>
            )}
            {booking.status === "cancelled" && isAdmin && (
              <button
                onClick={() => onReopen(booking)}
                data-testid={`reopen-btn-${booking.id}`}
                title="Reopen this cancelled booking (advance stays with customer)"
                className="p-1.5 border border-[#00B341] text-[#00B341] hover:bg-[#00B341] hover:text-white"
              >
                <ArrowsClockwise size={12} weight="bold" />
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-canvas-panel/30 border-b border-canvas-line">
          <td colSpan={9} className="px-6 py-4 space-y-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Payments
              </div>
              {(booking.payments || []).length === 0 ? (
                <div className="font-mono text-xs text-ink-subtle">No payments captured.</div>
              ) : (
                <table className="w-full text-xs" data-testid={`booking-payments-${booking.id}`}>
                  <thead>
                    <tr className="border-b border-canvas-line">
                      {["Date", "Method", "Ref", "Note", "Recorded By", "Amount"].map((h) => (
                        <th
                          key={h}
                          className="text-left px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-muted"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {booking.payments.map((p) => (
                      <tr key={p.id} className="border-b border-canvas-line last:border-b-0">
                        <td className="px-2 py-1 font-mono">{fmtDate(p.paid_on)}</td>
                        <td className="px-2 py-1 uppercase font-mono text-[10px]">{p.payment_method}</td>
                        <td className="px-2 py-1 font-mono text-[10px] text-ink-muted">{p.reference || "—"}</td>
                        <td className="px-2 py-1">{p.note || "—"}</td>
                        <td className="px-2 py-1 text-ink-muted">{p.recorded_by_name}</td>
                        <td className="px-2 py-1 font-mono text-right tabular-nums font-semibold">
                          {fmtMoney(p.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <RelatedVouchers context="booking" record={booking} defaultCollapsed={false} />
          </td>
        </tr>
      )}
    </>
  );
}

function ConvertDialog({ booking, vehicles, packages, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState("");
  // Build a quick lookup so the picker can show each VIN's attached package(s)
  // — operators recognise "JUPITER 125 BSVI-DISC" much faster than "TVS SCOOTERS".
  const packageMap = useMemo(() => {
    const m = {};
    (packages || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [packages]);

  // Show every available vehicle — operator may legitimately convert to a
  // different model or color than what was originally booked.
  const filtered = useMemo(() => {
    if (!booking) return [];
    const needle = q.trim().toLowerCase();
    return vehicles
      .filter((v) => v.status === "available")
      .filter((v) => {
        if (!needle) return true;
        const hay = [v.vin, v.package_name, v.year, v.color].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(needle);
      })
      .slice(0, 200);
  }, [vehicles, q, booking]);

  if (!booking) return null;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!picked) {
      toast.error("Pick a vehicle (VIN) first");
      return;
    }
    // Hand the picked VIN back to the parent. The parent then opens the full
    // SaleFormDrawer pre-filled with booking data so the user can layer on
    // exchange / finance / additional payment tenders before submission.
    onPick(picked);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        data-testid="convert-dialog"
        className="bg-canvas-card border-2 border-ink p-6 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            Convert Booking → Sale
          </div>
          <h2 className="font-display text-xl font-bold text-ink mt-1">
            Pick the vehicle (VIN) to deliver
          </h2>
          <div className="font-mono text-[10px] text-ink-muted mt-1">
            Booked as: <b>{booking.package_name || "—"}</b> · {booking.color || "—"} · {booking.customer?.name}
          </div>
          <div className="font-mono text-[10px] text-ink-subtle mt-1">
            You may convert to a different model / color if needed — any available
            vehicle works. Next step: capture exchange / finance / payment mix.
          </div>
        </div>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search VIN, package, color…"
          data-testid="convert-search"
          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
        />
        <div className="border border-canvas-line max-h-[360px] overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-8 text-center font-mono text-xs text-ink-subtle">
              No available vehicles match.
            </div>
          )}
          {filtered.map((v) => {
            const sel = picked === v.id;
            const attachedPkgs = (v.package_ids || []).map((id) => packageMap[id]).filter(Boolean);
            const pkgLabel = attachedPkgs.map((p) => p.name).join(" · ");
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setPicked(v.id)}
                data-testid={`convert-vehicle-${v.id}`}
                className={`block w-full text-left px-3 py-2 text-sm border-b border-canvas-line last:border-b-0 ${
                  sel ? "bg-canvas-panel border-l-4 border-l-ink" : "hover:bg-canvas-panel/60"
                }`}
              >
                <div className="flex justify-between items-center gap-2">
                  <span className="font-semibold text-ink truncate">
                    {v.package_name || v.model_category || "—"}
                  </span>
                  <span className="font-mono text-[10px] text-ink-muted tabular-nums">
                    {fmtMoney(v.price)}
                  </span>
                </div>
                {pkgLabel && (
                  <div className="text-[11px] text-ink truncate mt-0.5">
                    {pkgLabel}
                  </div>
                )}
                <div className="font-mono text-[10px] text-ink-subtle uppercase tracking-wider mt-0.5 truncate">
                  {v.color ? `${v.color} · ` : ""}VIN {v.vin}
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-canvas-line">
          <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!picked}
            data-testid="convert-confirm"
            className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
          >
            Continue to Sale Form →
          </button>
        </div>
      </form>
    </div>
  );
}

export default function BookingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [topUpTarget, setTopUpTarget] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [swapTarget, setSwapTarget] = useState(null);
  // When the user picks a VIN for a booking, we open the full SaleFormDrawer
  // pre-filled so they can layer on exchange / finance / extra payment lines
  // before submitting the (pending-approval) sale.
  const [convertSaleForm, setConvertSaleForm] = useState(null);
  const [convertSaleVehicles, setConvertSaleVehicles] = useState([]);
  const [convertSaleCustomers, setConvertSaleCustomers] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [modelFilter, setModelFilter] = useState("");
  // Default date filter: last 30 days. Filters bookings by created_at.
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(today); start.setDate(today.getDate() - 29);
    const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    return { from: ymd(start), to: ymd(today) };
  });

  // Build the booking-list query params from the current filter state.
  const _bookingParams = () => {
    const params = {};
    if (branchFilter) params.branch_id = branchFilter;
    if (statusFilter) params.status = statusFilter;
    if (modelFilter) params.model_category = modelFilter;
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    return params;
  };

  // Reload ONLY the bookings list — the vehicles / customers / packages caches
  // are stable across mutations (a new booking doesn't materially change them),
  // and on this dataset reloading all four lists adds ~10 MB and several
  // seconds of round-trip per save. Use this for routine after-save refreshes.
  const refreshBookings = async () => {
    try {
      const params = _bookingParams();
      const { data } = await api.get("/bookings", { params });
      setBookings(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = _bookingParams();
      const [b, v, c, p] = await Promise.all([
        api.get("/bookings", { params }),
        api.get("/vehicles", { params: { status: "available" } }),
        api.get("/customers", { params: { active_only: true } }),
        api.get("/packages", { params: { active_only: true } }),
      ]);
      setBookings(b.data);
      setVehicles(v.data);
      setCustomers(c.data);
      setPackages(p.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, statusFilter, modelFilter, dateRange.from, dateRange.to]);

  // Open the Sale form for a booking + a picked VIN, pre-filling everything
  // the booking already knows (customer, packages, advance, branch). The user
  // can then enable exchange / finance / add payment lines as needed.
  const openSaleFromBooking = async (booking, vehicleId) => {
    // Make sure the picked vehicle is in the picker list, even if it was
    // reserved by this very booking and thus not in `vehicles` (which only
    // loads status=available).
    let vehiclesForForm = vehicles;
    if (!vehicles.find((v) => v.id === vehicleId)) {
      try {
        const { data } = await api.get(`/vehicles/${vehicleId}`);
        vehiclesForForm = [data, ...vehicles];
      } catch {
        // Best-effort. If the fetch fails, leave the list as-is; the user
        // will see an empty vehicle picker and can re-select.
      }
    }
    const paid = Number(booking.total_paid) || 0;
    // On-road price is ALWAYS recomputed from the selected packages at
    // conversion time — neither the booking's quoted total nor the vehicle's
    // inventory list price is used. The package picker's auto-sum (in
    // SaleFormDrawer) populates this field as soon as the packages load.
    // Rationale: package masters get updated (price revisions, new mandatory
    // items, GST changes) between booking and delivery — the operator
    // expects the current catalog price, not the stale booking quote.
    const onRoad = "";
    setConvertSaleVehicles(vehiclesForForm);
    // Same defensive merge for the customer: bookings can carry customers who
    // are no longer in the "active_only" customer list (e.g. previously 'won').
    let customersForForm = customers;
    if (booking.customer && !customers.find((c) => c.id === booking.customer_id)) {
      customersForForm = [booking.customer, ...customers];
    }
    setConvertSaleCustomers(customersForForm);
    setConvertSaleForm({
      vehicle_id: vehicleId,
      customer_id: booking.customer_id,
      package_ids: booking.package_ids || [],
      gst_type: booking.gst_type || "intra",
      branch_id: booking.branch_id || "",
      advance_amount: paid,
      on_road_price: onRoad,
      booking_id: booking.id,
      notes: `Converted from booking ${booking.id.slice(0, 8)}.`,
      _advance_from_bookings:
        paid > 0 ? { count: 1, total: paid } : null,
    });
    setConvertTarget(null);
  };

  const handleConvert = async (booking) => {
    // If a vehicle was pre-assigned at booking time (legacy bookings), skip
    // the picker and jump straight into the Sale form pre-filled with that VIN.
    // Otherwise show the VIN picker first.
    if (booking.vehicle_id) {
      await openSaleFromBooking(booking, booking.vehicle_id);
    } else {
      setConvertTarget(booking);
    }
  };

  const handleCancel = async (booking) => {
    if (!window.confirm(`Cancel booking for ${booking.customer?.name}? Vehicle will be released.`)) return;
    try {
      await api.post(`/bookings/${booking.id}/cancel`);
      toast.success("Booking cancelled");
      refreshBookings();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReopen = async (booking) => {
    if (!window.confirm(`Reopen this cancelled booking for ${booking.customer?.name}? The advance stays with the customer and the vehicle will be re-reserved if available.`)) return;
    try {
      await api.post(`/bookings/${booking.id}/reopen`);
      toast.success("Booking reopened — status is now Active");
      refreshBookings();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="bookings-page">
      <PageHeader
        eyebrow="03 / Bookings"
        title="Vehicle Bookings"
        subtitle="Reserve vehicles, collect advances over time, and convert to a sale at delivery. Cancellations are admin-only."
        actions={
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="new-booking-button"
            className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
          >
            <Plus size={14} weight="bold" />
            New Booking
          </button>
        }
      />

      <div className="px-8 py-6">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex border border-canvas-line" data-testid="booking-status-filter">
            {STATUS_FILTERS.map((opt) => {
              const active = statusFilter === opt.v;
              return (
                <button
                  key={opt.v || "all"}
                  type="button"
                  onClick={() => setStatusFilter(opt.v)}
                  data-testid={`booking-status-filter-${opt.v || "all"}`}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${
                    active ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"
                  }`}
                >
                  {opt.l}
                </button>
              );
            })}
          </div>
          <BranchFilter value={branchFilter} onChange={setBranchFilter} testId="bookings-branch-filter" kind="sales" />
          <ModelCategoryFilter value={modelFilter} onChange={setModelFilter} testId="bookings-model-filter" />
          <DateRangeFilter value={dateRange} onChange={setDateRange} testIdPrefix="bookings-date" />
        </div>

        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="bookings-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  <th className="w-10 px-4 py-3" />
                  {["Date", "Vehicle", "Customer", "Advance Paid", "Status", ""].map((h) => (
                    <th
                      key={h || "act"}
                      className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && bookings.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center">
                      <Ticket size={24} weight="bold" className="text-ink-subtle mx-auto mb-2" />
                      <div className="font-mono text-xs text-ink-subtle mb-2">No bookings yet.</div>
                      <button
                        onClick={() => setDrawerOpen(true)}
                        className="text-sm font-semibold text-brand hover:underline"
                      >
                        + Create your first booking
                      </button>
                    </td>
                  </tr>
                )}
                {bookings.map((b) => (
                  <BookingRow
                    key={b.id}
                    booking={b}
                    isAdmin={isAdmin}
                    onTopUp={(bk) => setTopUpTarget(bk)}
                    onConvert={handleConvert}
                    onCancel={handleCancel}
                    onSwap={(bk) => setSwapTarget(bk)}
                    onReopen={handleReopen}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <BookingFormDrawer
        open={drawerOpen}
        vehicles={vehicles}
        customers={customers}
        packages={packages}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          setDrawerOpen(false);
          refreshBookings();
        }}
      />

      <TopUpDialog
        open={!!topUpTarget}
        booking={topUpTarget || { balance_due: 0 }}
        onClose={() => setTopUpTarget(null)}
        onDone={() => {
          setTopUpTarget(null);
          refreshBookings();
        }}
      />

      <ConvertDialog
        booking={convertTarget}
        vehicles={vehicles}
        packages={packages}
        onClose={() => setConvertTarget(null)}
        onPick={(vehicleId) => openSaleFromBooking(convertTarget, vehicleId)}
      />

      {/* Pre-filled Sale form when converting a booking — lets the user
          capture exchange / finance / payment lines on top of the booking's
          carried-over advance before submitting. */}
      <SaleFormDrawer
        open={!!convertSaleForm}
        vehicles={convertSaleVehicles}
        customers={convertSaleCustomers}
        initialForm={convertSaleForm}
        onClose={() => setConvertSaleForm(null)}
        onSaved={() => {
          setConvertSaleForm(null);
          toast.success("Sale created from booking (pending approval)");
          refreshBookings();
        }}
      />

      <SwapVehicleDialog
        open={!!swapTarget}
        sourceType="booking"
        sourceId={swapTarget?.id}
        currentVehicleId={swapTarget?.vehicle_id}
        currentVin={swapTarget?.vehicle?.vin}
        onClose={() => setSwapTarget(null)}
        onSwapped={() => {
          setSwapTarget(null);
          refreshBookings();
        }}
      />
    </div>
  );
}
